import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { User } from '../../types/index'

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = !!action.payload
      // Sync with localStorage
      localStorage.setItem('auth-user', JSON.stringify(action.payload))
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
      state.isAuthenticated = !!action.payload && !!state.user
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.isLoading = false
    },
    login: (
      state,
      action: PayloadAction<{
        user: User
        token: string
        refreshToken?: string
      }>
    ) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken || null
      state.isAuthenticated = true
      state.isLoading = false
      state.error = null

      // Save to LocalStorage
      localStorage.setItem('auth-token', action.payload.token)
      localStorage.setItem('auth-user', JSON.stringify(action.payload.user))
      if (action.payload.refreshToken) {
        localStorage.setItem('auth-refresh-token', action.payload.refreshToken)
      }
    },

    logout: state => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.isLoading = false
      state.error = null

      localStorage.removeItem('auth-token')
      localStorage.removeItem('auth-refresh-token')
      localStorage.removeItem('auth-user')
    },
    clearError: state => {
      state.error = null
    },

    // ✅ Load saved session on app refresh
    initializeAuth: state => {
      const token = localStorage.getItem('auth-token')
      const refreshToken = localStorage.getItem('auth-refresh-token')
      const userStr = localStorage.getItem('auth-user')

      if (token && refreshToken && userStr) {
        try {
          const user = JSON.parse(userStr)
          state.user = user
          state.token = token
          state.refreshToken = refreshToken
          state.isAuthenticated = true
        } catch (error) {
          console.warn('Failed to parse auth-user from localStorage', error)
          localStorage.removeItem('auth-token')
          localStorage.removeItem('auth-refresh-token')
          localStorage.removeItem('auth-user')
        }
      }
    },
  },
})

export const {
  login,
  logout,
  setLoading,
  setError,
  clearError,
  initializeAuth,
  setUser,
} = authSlice.actions

export default authSlice.reducer
